import { DOT_LEVELS } from '@/lib/dots/palette'

// Values below LOW_CUT are ordered-dithered into sparse specks, which gives the soft halftone edges.
export const LOW_CUT = 0.14

const BAYER_4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5]

const MAX_LEVEL = DOT_LEVELS.length

export const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

export const smoothstep = (edge0: number, edge1: number, value: number): number => {
    const t = clamp01((value - edge0) / (edge1 - edge0))

    return t * t * (3 - 2 * t)
}

export const toLevel = (value: number, col: number, row: number): number => {
    if (value >= LOW_CUT) {
        const level = 2 + Math.floor(((value - LOW_CUT) / (1 - LOW_CUT)) * (MAX_LEVEL - 1))

        return level > MAX_LEVEL ? MAX_LEVEL : level
    }

    if (value <= 0.015) return 0

    const threshold = (BAYER_4[((row & 3) << 2) | (col & 3)] + 0.5) / 16

    return value / LOW_CUT > threshold ? 1 : 0
}

export const paintDots = (
    ctx: CanvasRenderingContext2D,
    levels: Uint8Array,
    cols: number,
    rows: number,
    cellPx: number,
    colors: Array<string>
): void => {
    for (let level = 1; level <= MAX_LEVEL; level++) {
        const size = Math.max(1, Math.round(cellPx * DOT_LEVELS[level - 1].size))
        const offset = Math.floor((cellPx - size) / 2)

        let drawn = false

        ctx.beginPath()

        for (let row = 0, index = 0; row < rows; row++) {
            const y = row * cellPx + offset

            for (let col = 0; col < cols; col++, index++) {
                if (levels[index] !== level) continue

                ctx.rect(col * cellPx + offset, y, size, size)
                drawn = true
            }
        }

        if (drawn) {
            ctx.fillStyle = colors[level - 1]
            ctx.fill()
        }
    }
}

// A faint dot at the center of every cell, rendered once per resize and blitted each frame.
export const createGridLayer = (
    width: number,
    height: number,
    cols: number,
    rows: number,
    cellPx: number,
    color: string
): HTMLCanvasElement | null => {
    const layer = document.createElement('canvas')
    layer.width = width
    layer.height = height

    const ctx = layer.getContext('2d')

    if (!ctx) return null

    const size = Math.max(1, Math.round(cellPx * 0.14))
    const offset = Math.floor((cellPx - size) / 2)

    ctx.fillStyle = color
    ctx.beginPath()

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            ctx.rect(col * cellPx + offset, row * cellPx + offset, size, size)
        }
    }

    ctx.fill()

    return layer
}
